"use client";

import React from "react";
import { BlurFade } from "@/components/ui/blur-fade";
import Image from "next/image";
import { ButtonLink } from "@/components/ui/button-link";

interface JourneyHeroProps {
  title: string;
  description: string;
  ctaText: string;
  ctaHref?: string;
  ctaSubtext: string;
  backgroundImage: string;
}

const JourneyHero = ({
  title,
  description,
  ctaText,
  ctaHref,
  ctaSubtext,
  backgroundImage,
}: JourneyHeroProps) => {
  return (
    <section className="relative flex min-h-[90vh] flex-col items-center overflow-visible bg-white pb-0 pt-28">
      <div className="container relative z-20 mx-auto max-w-[1600px] px-4 lg:px-6">
        {/* Main Hero Card - Surfshark Style */}
        <div className="relative flex flex-col items-center overflow-hidden rounded-[3rem] bg-secondary pt-12 shadow-sm lg:rounded-[4rem] lg:pt-20">
          {/* Creative Background Pattern - White Radial Waves */}
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden">
            <div className="absolute aspect-square w-[140%] rounded-full border-[1px] border-white/40" />
            <div className="absolute aspect-square w-[110%] rounded-full border-[1px] border-white/30" />
            <div className="absolute aspect-square w-[80%] rounded-full border-[1px] border-white/20" />
            <div className="absolute aspect-square w-[50%] rounded-full border-[1px] border-white/10" />
            <div className="absolute aspect-square w-[30%] rounded-full border-[1px] border-white/5" />
          </div>

          <div className="relative z-10 flex max-w-5xl flex-col items-center gap-4 px-6 text-center">
            <BlurFade delay={0.1}>
              <h1 className="text-3xl font-bold leading-[1.05] tracking-tight text-foreground md:text-5xl lg:text-[4rem]">
                {title}
              </h1>
            </BlurFade>

            <BlurFade delay={0.2}>
              <p className="max-w-xl text-base font-medium leading-relaxed text-foreground/70 lg:text-lg">
                {description}
              </p>
            </BlurFade>

            <BlurFade
              delay={0.3}
              className="mt-2 flex flex-col items-center gap-4"
            >
              <ButtonLink href={ctaHref} variant="primary" size="md">
                {ctaText}
              </ButtonLink>
              <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-foreground/60">
                <div className="flex h-4 w-4 items-center justify-center rounded-full border border-foreground/20 text-[10px]">
                  ↺
                </div>
                {ctaSubtext}
              </div>
            </BlurFade>
          </div>

          {/* Hero Image - Shifted up for better visibility */}
          {backgroundImage ? (
            <BlurFade
              delay={0.4}
              className="relative mt-0 w-full max-w-[1400px] translate-y-12 px-6 lg:mt-1 lg:px-0"
            >
              <div className="relative aspect-[16/9] w-full lg:aspect-[21/10]">
                <Image
                  src={backgroundImage}
                  alt="Tiera Interface"
                  fill
                  className="object-contain object-bottom"
                  priority
                />
              </div>
            </BlurFade>
          ) : (
            <div className="mt-8 lg:mt-12" />
          )}
        </div>
      </div>
    </section>
  );
};

export default JourneyHero;
